import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { templateHeaderSectionRelayFragment$key } from "@/__generated__/templateHeaderSectionRelayFragment.graphql.ts";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { wacToken } from "@/libraries/design/foundations";

import TemplateCtaButtonsCfind3646 from "../../cfind3646-sections/cfind3646-template-cta-buttons";
import { TemplateCtaButtons } from "../../template-details-page/template-cta-buttons";
import { TemplateAuthor } from "../components/template-author-relay";

const headerSectionStyles = css({
    borderBlockEndStyle: "solid",
    borderBlockEndWidth: "1px",
    borderBlockEndColor: token("color.background.accent.gray.subtler.hovered"),
    display: "flex",
    flexDirection: "column",
    paddingBlockEnd: "24px",
    [media.below.sm]: {
        alignItems: "center"
    }
});

const headerSectionStylesCfind3646 = css({
    borderBlockEndColor: token("color.background.accent.gray.subtler.hovered"),
    display: "flex",
    flexDirection: "column",
    paddingBlockEnd: "24px",
    [media.below.sm]: {
        alignItems: "center"
    }
});

const titleStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#172B4D",
    fontFamily: "Charlie Display",
    fontSize: "52px",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "60px",
    marginBlockEnd: "16px",
    marginBlockStart: 0,
    overflowWrap: "break-word",
    [media.below.sm]: {
        textAlign: "center"
    }
});

const titleStylesCfind3646 = css({
    color: wacToken("color.extended.blue1000"),
    fontFamily: "Charlie Display",
    fontSize: "52px",
    fontStyle: "normal",
    fontWeight: "800",
    lineHeight: "60px",
    marginBlockEnd: "16px",
    marginBlockStart: 0,
    overflowWrap: "break-word",
    [media.below.sm]: {
        textAlign: "center"
    }
});

const subTitleStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#172B4D",
    fontFamily: "Charlie Text",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "28px",
    marginBlockEnd: "32px",
    marginBlockStart: 0,
    [media.below.sm]: {
        textAlign: "center"
    }
});

const subTitleStylesCfind3646 = css({
    color: wacToken("color.extended.blue1000"),
    fontFamily: "Charlie Text",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "28px",
    marginBlockStart: 0,
    [media.below.sm]: {
        textAlign: "center"
    }
});

const TemplateHeaderSectionRelayFragment = graphql`
    fragment templateHeaderSectionRelayFragment on ContentPlatformTemplate {
        name
        oneLinerHeadline
        pluginModuleKey
        ...templateAuthorRelayFragment
    }
`;

export type TemplateHeaderSectionProps = {
    template: templateHeaderSectionRelayFragment$key;
    isCfind3646Treatment: boolean;
};

export const TemplateHeaderSection = ({
    template: templateRef,
    isCfind3646Treatment: isCfind3646Treatment
}: TemplateHeaderSectionProps) => {
    const template = useFragment(
        TemplateHeaderSectionRelayFragment,
        templateRef
    );
    const { name, oneLinerHeadline, pluginModuleKey } = template;

    const intl = useIntl();

    const templateName = intl.formatMessage(
        ConfluenceTemplateMessages.templateTitle,
        {
            templateName: name ?? ""
        }
    );

    const renderCtaButons = () => {
        if (isCfind3646Treatment) {
            return (
                <TemplateCtaButtonsCfind3646
                    pluginModuleKey={template.pluginModuleKey || ""}
                />
            );
        } else
            return (
                <TemplateCtaButtons
                    name={templateName}
                    pluginModuleKey={pluginModuleKey || ""}
                />
            );
    };

    return (
        <section
            css={[
                isCfind3646Treatment
                    ? headerSectionStylesCfind3646
                    : headerSectionStyles
            ]}
            data-testid="TemplateHeaderSection"
        >
            <h1
                css={[
                    isCfind3646Treatment ? titleStylesCfind3646 : titleStyles
                ]}
            >
                {templateName}
            </h1>
            <TemplateAuthor
                template={template}
                isCfind3646Treatment={isCfind3646Treatment}
            />
            <p
                css={[
                    isCfind3646Treatment
                        ? subTitleStylesCfind3646
                        : subTitleStyles
                ]}
            >
                {oneLinerHeadline}
            </p>
            {renderCtaButons()}
        </section>
    );
};
